import { COLOR_BY_CODE, PARTIES_DEFAULT, PARTY_DEFAULT_COLOR } from './constants.js';

/**
 * Build a `partyColor(name, code)` resolver that:
 *   1. Prefers the numeric "code" palette (mirrors the map's color_styles.css).
 *   2. Falls back to per-party overrides passed by the caller.
 *   3. Returns PARTY_DEFAULT_COLOR if nothing matches.
 *
 * Why a factory? Each chart instance can receive an `overrides` object via
 * EPScatter.mount({ parties: {...} }) — keeping this function pure means
 * components can rely on stable references in $derived().
 */
export function buildPartyHelpers(overrides = {}) {
  const dict = { ...PARTIES_DEFAULT, ...overrides };

  function partyColor(name, code) {
    if (code != null && COLOR_BY_CODE[code]) return COLOR_BY_CODE[code];
    if (overrides[name]?.color) return overrides[name].color;
    return PARTY_DEFAULT_COLOR;
  }

  function partyLongName(name) {
    return dict[name]?.long || name;
  }

  return { partyColor, partyLongName };
}

/**
 * Pick a readable text colour (white or near-black) over an arbitrary
 * background, using WCAG-ish relative luminance. Used by the tooltip
 * header which inherits the winning party's colour.
 */
export function textOn(hex) {
  const c = (hex || '').replace('#', '');
  if (c.length < 6) return '#fff';
  const toLin = (s) => {
    const v = parseInt(s, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const r = toLin(c.substr(0, 2));
  const g = toLin(c.substr(2, 2));
  const b = toLin(c.substr(4, 2));
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.55 ? '#1a1a1a' : '#fff';
}
